import React, { useState, useRef, useEffect, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Collapse, ConfigProvider, Flex } from '@app/ui';
import SelectedFiltersTags from '@app/components/filters/components/SelectedFiltersTags';
import { SocialBenchmarkingContext } from './SocialBenchmarkingContext';
import {
    getTranslatedSocialMetricOptions,
    getTranslatedDatePeriodicityOptions,
    getTranslatedVisualizationOptions,
    getTranslatedVisualizationOptionsFollowers,
    platformOptions,
    allAccountTypeOptions,
} from './socialbenchmarking-filters';

export type FilterSelectionType = {
    title: string;
    selections: string[];
    displayValues?: string[];
};

type FilterContainerProps = {
    children: React.ReactNode;
    leagues?: any[];
};

const FiltersContainer = ({ children, leagues }: FilterContainerProps) => {
    const { t } = useTranslation(['common', 'benchmarking']);
    const { appliedFilters, mediaOptions } = useContext(
        SocialBenchmarkingContext,
    );

    // Get translated options
    const socialMetricOptions = useMemo(
        () => getTranslatedSocialMetricOptions(t),
        [t],
    );
    const datePeriodicityOptions = useMemo(
        () => getTranslatedDatePeriodicityOptions(t),
        [t],
    );
    const visualizationOptions = useMemo(
        () => getTranslatedVisualizationOptions(t),
        [t],
    );
    const visualizationOptionsFollowers = useMemo(
        () => getTranslatedVisualizationOptionsFollowers(t),
        [t],
    );

    // Helper functions to convert IDs to display names
    const getSocialMetricName = (value: string) => {
        return (
            socialMetricOptions.find(option => option.value === value)?.name ||
            value
        );
    };

    const getDatePeriodicityName = (value: string) => {
        return (
            datePeriodicityOptions.find(option => option.value === value)
                ?.name || value
        );
    };

    const getPlatformNames = (platformIds: number[]) => {
        if (platformIds.length === 0) return ['All'];
        return platformIds.map(
            id =>
                platformOptions.find(option => option.value === id)?.label ||
                id.toString(),
        );
    };

    const getAccountTypeNames = (accountTypeIds: number[]) => {
        if (accountTypeIds.length === 0) return ['All'];
        return accountTypeIds.map(
            id =>
                allAccountTypeOptions.find(option => option.value === id)
                    ?.label || id.toString(),
        );
    };

    const getLeagueName = (leagueId: string | null) => {
        if (!leagueId) return null;
        if (leagueId.startsWith('favorites_')) {
            return t('socialBenchmarking.filters.favorites', {
                ns: 'benchmarking',
            });
        }

        const league = leagues?.find(l => l.id.toString() === leagueId);
        return league?.name || leagueId;
    };

    const getVisualizationName = (value: string) => {
        // Check both regular and followers variants
        const allVisualizationOptions = [
            ...visualizationOptions,
            ...visualizationOptionsFollowers,
        ];
        const option = allVisualizationOptions.find(
            option => option.value === value,
        );
        return option?.name || value;
    };

    const getMediaTypesNames = (mediaTypeIds: number[]) => {
        if (mediaTypeIds.length === 0) return ['All'];
        return mediaTypeIds.map(
            id =>
                mediaOptions.find(option => option.value === id)?.label ||
                id.toString(),
        );
    };

    // Build filter selections from applied filters
    const filterSelection = [
        {
            title: t('filters.socialMetric'),
            selections: [
                getSocialMetricName(appliedFilters.socialMetric),
            ].filter(Boolean),
        },
        {
            title: t('filters.league'),
            selections: [getLeagueName(appliedFilters.league)].filter(Boolean),
        },
        {
            title: t('filters.visualizationType'),
            selections: [
                getVisualizationName(appliedFilters.visualization),
            ].filter(Boolean),
        },
        {
            title: t('filters.dateRange'),
            selections: [
                getDatePeriodicityName(appliedFilters.datePeriodicity),
            ].filter(Boolean),
        },

        {
            title: t('filters.platforms'),
            selections: getPlatformNames(appliedFilters.platforms),
        },
        {
            title: t('filters.accountTypes'),
            selections: getAccountTypeNames(appliedFilters.accountTypes),
        },
        {
            title: t('filters.mediaTypes'),
            selections: getMediaTypesNames(appliedFilters.mediaTypes),
        },
    ].filter(filter => filter.selections.length > 0);

    const [isExpanded, setIsExpanded] = useState(true);
    const stickyRef = useRef<HTMLDivElement>(null);
    const [isAtTop, setIsAtTop] = useState(false);

    const collapseFilters = () => {
        setIsExpanded(false);
    };

    useEffect(() => {
        const handleScroll = () => {
            if (stickyRef.current) {
                const top = stickyRef.current.getBoundingClientRect().top;
                setIsAtTop(top <= 60);
            }
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll();

        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const titleContent = (
        <Flex>
            <Flex
                gap={12}
                justify="center"
                align="center"
                className="-m-r-2"
                style={{
                    height: 'fit-content',
                    fontSize: 16,
                }}
            >
                <i className="fa-regular fa-sliders" />
                {t('filters.title')}
            </Flex>
            <Flex
                style={{
                    display: 'flex',
                    flex: 1,
                }}
            >
                <SelectedFiltersTags
                    selectedFiltersArray={filterSelection.map(selection => ({
                        title: selection.title,
                        elements: selection.selections,
                    }))}
                />
            </Flex>
        </Flex>
    );

    return (
        <ConfigProvider
            theme={{
                components: {
                    Collapse: {
                        headerBg: !isAtTop ? '#FAFAFA' : '#FFF',
                        colorBorder: !isAtTop ? '#BFBFBF' : '#FFF',
                    },
                },
            }}
        >
            <Collapse
                className="sticky"
                ref={stickyRef}
                style={{
                    top: 60,
                    position: 'sticky',
                    overflow: 'hidden',
                    ...(isAtTop && {
                        borderTop: 'none',
                        borderLeft: 'none',
                        borderRight: 'none',
                        width: 'calc(100% + 48px)',
                        transform: 'translateX(-24px)',
                        borderRadius: '0',
                        boxShadow: '0 4px 4px -2px rgba(0, 0, 0, 0.25)',
                    }),
                }}
                activeKey={isExpanded ? ['1'] : []}
                onChange={() => setIsExpanded(!isExpanded)}
                expandIconPosition="end"
                items={[
                    {
                        key: '1',
                        label: titleContent,
                        children: (
                            <div className="filters-accordion__selects">
                                {React.cloneElement(
                                    children as React.ReactElement,
                                    {
                                        onCollapseFilters: collapseFilters,
                                    },
                                )}
                            </div>
                        ),
                    },
                ]}
            />
        </ConfigProvider>
    );
};

export default FiltersContainer;
